import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "../../context/StoreContext";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Select from "../../components/common/Select";
import Textarea from "../../components/common/Textarea";
import Checkbox from "../../components/common/Checkbox";
import Alert from "../../components/common/Alert";
import Modal from "../../components/common/Modal";
import PageHeader from "../../components/common/PageHeader";
import Table from "../../components/common/Table";
import Card from "../../components/common/Card";
import {
  useGetOpenTillSessionsQuery,
  useGetTillsQuery,
  useGetUsersQuery,
  useOpenTillSessionMutation,
} from "../../store/api";
import { useDeviceIdentity } from "../../hooks/useDeviceIdentity";
import { getCashSessionDeviceName } from "../../hooks/useDeviceCashSession";
import { getCashingUpApprovalStatusLabel } from "../../utils/cashingUp";

const initialForm = {
  openingCash: "",
  businessDate: "",
  deviceName: "",
  openedByUserId: "",
  notes: "",
  includeOptionalFields: false,
};

const getErrorMessage = (err, fallback) =>
  err?.data?.detail ||
  err?.data?.message ||
  err?.message ||
  fallback ||
  "Something went wrong.";

const CashingUpSessions = () => {
  const navigate = useNavigate();
  const { selectedStoreId } = useStore();
  const { device: currentDevice } = useDeviceIdentity(Boolean(selectedStoreId));
  const [openModal, setOpenModal] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [formErrors, setFormErrors] = useState({});
  const [localError, setLocalError] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [tillFilter, setTillFilter] = useState("all");

  const {
    data: tillsData,
  } = useGetTillsQuery(
    { storeId: selectedStoreId, activeOnly: true },
    { skip: !selectedStoreId },
  );
  const { data: usersData } = useGetUsersQuery();

  const {
    data: sessionsData,
    isLoading: sessionsLoading,
    isError: sessionsError,
    error: sessionsErrorDetail,
    refetch,
  } = useGetOpenTillSessionsQuery(selectedStoreId, {
    skip: !selectedStoreId,
  });

  const [openTillSession, openState] = useOpenTillSessionMutation();

  const tills = useMemo(
    () => (Array.isArray(tillsData) ? tillsData : []),
    [tillsData],
  );
  const tillById = useMemo(
    () => new Map(tills.map((till) => [till.tillId, till])),
    [tills],
  );
  const currentDeviceName = getCashSessionDeviceName(currentDevice);
  const users = useMemo(
    () => (Array.isArray(usersData) ? usersData : []),
    [usersData],
  );
  const usersById = useMemo(
    () => new Map(users.map((user) => [user.userId, user])),
    [users],
  );
  const getOpenedByLabel = (session) => {
    const user = usersById.get(session?.openedByUserId);
    return (
      session?.openedByName ||
      session?.openedByUserName ||
      user?.displayName ||
      user?.name ||
      user?.fullName ||
      session?.openedBy ||
      session?.openedByUserId ||
      "-"
    );
  };
  const getTillLabel = (session) => {
    const till = tillById.get(session?.tillId);
    return (
      session?.tillName ||
      till?.tillName ||
      till?.tillCode ||
      session?.tillId ||
      "-"
    );
  };
  const sessions = useMemo(
    () => (Array.isArray(sessionsData) ? sessionsData : []),
    [sessionsData],
  );

  const filteredSessions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return sessions.filter((session) => {
      const searchable = [
        session.cashSessionId,
        session.tillName,
        session.tillId,
        tillById.get(session.tillId)?.tillName,
        tillById.get(session.tillId)?.tillCode,
        session.businessDate,
        session.openedByName,
        session.openedByUserName,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const matchesQuery = normalizedQuery
        ? searchable.includes(normalizedQuery)
        : true;
      const matchesStatus =
        statusFilter === "all"
          ? true
          : String(session.status || "").toLowerCase() === statusFilter;
      const matchesTill =
        tillFilter === "all"
          ? true
          : String(session.tillId || "").toLowerCase() === tillFilter;
      return matchesQuery && matchesStatus && matchesTill;
    });
  }, [query, sessions, statusFilter, tillFilter, tillById]);

  useEffect(() => {
    if (!selectedStoreId) {
      setForm(initialForm);
      setOpenModal(false);
      return;
    }
    setForm((prev) => ({
      ...prev,
      businessDate: prev.businessDate || new Date().toISOString().slice(0, 10),
    }));
  }, [selectedStoreId]);

  const resetForm = () => {
    setForm({
      ...initialForm,
      businessDate: new Date().toISOString().slice(0, 10),
      deviceName: currentDeviceName || "",
    });
    setFormErrors({});
    setLocalError("");
  };

  const openNewSession = () => {
    resetForm();
    setOpenModal(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormErrors({});
    setLocalError("");
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validateForm = () => {
    const errors = {};
    if (form.openingCash === "" || Number.isNaN(Number(form.openingCash))) {
      errors.openingCash = "Opening cash must be a valid number.";
    }
    if (!form.businessDate) errors.businessDate = "Business date is required.";
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStoreId) {
      setLocalError("Please select a store first.");
      return;
    }

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const payload = {
      openingCash: Number(form.openingCash),
      deviceName: form.deviceName.trim() || currentDeviceName || null,
      openedByUserId: form.includeOptionalFields
        ? form.openedByUserId.trim() || null
        : null,
      businessDate: form.businessDate || null,
      notes: form.notes.trim() || null,
    };

    try {
      const created = await openTillSession({
        payload,
      }).unwrap();
      setOpenModal(false);
      resetForm();
      if (created?.cashSessionId) {
        navigate(`/cash-management/till-sessions/${created.cashSessionId}`);
      } else {
        refetch();
      }
    } catch (err) {
      setLocalError(getErrorMessage(err, "Failed to open till session."));
    }
  };

  const columns = [
    { key: "till", label: "Till Name" },
    { key: "openedBy", label: "Opened By" },
    { key: "openedAt", label: "Opened At" },
    { key: "status", label: "Status" },
    { key: "approval", label: "Approval" },
    { key: "syncStatus", label: "Sync Status" },
    { key: "actions", label: "Actions" },
  ];

  const rows = filteredSessions.map((session) => ({
    id: session.cashSessionId,
    till: (
      <button
        type="button"
        onClick={() =>
          navigate(`/cash-management/till-sessions/${session.cashSessionId}`)
        }
        className="font-semibold text-indigo-700 hover:underline dark:text-indigo-300"
      >
        {getTillLabel(session)}
      </button>
    ),
    openedBy: getOpenedByLabel(session),
    openedAt: session.openedAt
      ? new Date(session.openedAt).toLocaleString()
      : "-",
    status: (
      session.status ? (
        <span className="inline-flex rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-blue-700 dark:border-blue-900/60 dark:bg-blue-950/40 dark:text-blue-200">
          {session.status}
        </span>
      ) : (
        <span className="text-slate-500">-</span>
      )
    ),
    approval: (
      session.approvalStatus ? (
        <span
          className={`inline-flex rounded-md px-2.5 py-1 text-xs font-semibold uppercase tracking-wide ${
            session.approvalStatus === "NOT_REQUIRED"
              ? "border border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-300"
              : "border border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200"
          }`}
        >
          {getCashingUpApprovalStatusLabel(session.approvalStatus)}
        </span>
      ) : (
        <span className="text-slate-500">-</span>
      )
    ),
    syncStatus:
      session.syncStatus || session.isSynced ? (
        <span className="inline-flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-200">
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
          {session.syncStatus || "Synced"}
        </span>
      ) : (
        <span className="inline-flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200">
          <span className="h-2.5 w-2.5 rounded-full bg-amber-500" />
          Not Synced
        </span>
      ),
    actions: (
      <div className="flex items-center gap-2">
        <button
          type="button"
          title="View"
          onClick={() =>
            navigate(`/cash-management/till-sessions/${session.cashSessionId}`)
          }
          className="rounded-full p-2 text-indigo-600 transition hover:bg-indigo-50 dark:text-indigo-300 dark:hover:bg-indigo-950/40"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        </button>
      </div>
    ),
  }));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Till Sessions"
        subtitle="View and manage till sessions."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onClick={() => refetch()}>
              Refresh
            </Button>
            <Button size="sm" onClick={openNewSession} disabled={!selectedStoreId}>
              Open Till Session
            </Button>
          </div>
        }
      />

      {!selectedStoreId && (
        <Card>
          <Alert tone="warning">
            No store is selected. Please select a store before opening or
            reviewing till sessions.
          </Alert>
        </Card>
      )}

      {selectedStoreId && (
        <div className="space-y-6">
          <Card className="space-y-4 p-0">
            <div className="border-b border-slate-200 px-6 py-4 dark:border-slate-700">
              <div className="grid gap-4 md:grid-cols-[minmax(260px,1.4fr)_minmax(180px,0.9fr)_minmax(180px,0.9fr)_auto]">
                <Input
                  label="Search by till name..."
                  name="query"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <Select
                  label="All Status"
                  name="statusFilter"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="open">Open</option>
                  <option value="cash_up_in_progress">Cash-up in progress</option>
                  <option value="count_submitted">Count submitted</option>
                  <option value="closed">Closed</option>
                </Select>
                <Select
                  label="All Till"
                  name="tillFilter"
                  value={tillFilter}
                  onChange={(e) => setTillFilter(e.target.value)}
                >
                  <option value="all">All Till</option>
                  {tills.map((till) => (
                    <option key={till.tillId} value={till.tillId}>
                      {till.tillName || till.tillId}
                    </option>
                  ))}
                </Select>
                <div className="flex items-end">
                  <Button
                    size="sm"
                    variant="secondary"
                    className="h-11 whitespace-nowrap px-4"
                    onClick={() => {
                      setQuery("");
                      setStatusFilter("all");
                      setTillFilter("all");
                    }}
                  >
                    Reset Filters
                  </Button>
                </div>
              </div>
            </div>

            <div className="px-0 pb-4 pt-0">
              {sessionsLoading && (
                <p className="px-6 pt-4 text-sm text-slate-500 dark:text-slate-400">
                  Loading sessions...
                </p>
              )}
              {sessionsError && (
                <div className="px-6 pt-4">
                  <Alert tone="error">
                    {getErrorMessage(
                      sessionsErrorDetail,
                      "Failed to load open sessions.",
                    )}
                  </Alert>
                </div>
              )}
              {localError && (
                <div className="px-6 pt-4">
                  <Alert tone="error">{localError}</Alert>
                </div>
              )}
              {!sessionsLoading && !sessionsError && (
                <div className="px-0 pt-0">
                  <Table
                    columns={columns}
                    rows={rows}
                    emptyText="No till sessions match the current filters."
                  />
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      <Modal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        title="Open Till Session"
        confirmText={openState.isLoading ? "Opening..." : "Open Session"}
        onConfirm={handleSubmit}
        size="xl"
        isLoading={openState.isLoading}
      >
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Device Name"
              name="deviceName"
              value={form.deviceName}
              onChange={handleChange}
              helper="This will be sent as deviceName when opening the session."
              required
            />
            <Input
              label="Opening Cash"
              name="openingCash"
              type="number"
              value={form.openingCash}
              onChange={handleChange}
              error={formErrors.openingCash}
              required
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Business Date"
              name="businessDate"
              type="date"
              value={form.businessDate}
              onChange={handleChange}
              error={formErrors.businessDate}
              required
            />
          </div>

          <Checkbox
            label="Show Optional Fields"
            name="includeOptionalFields"
            checked={form.includeOptionalFields}
            onChange={handleChange}
          />

          {form.includeOptionalFields && (
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Opened By User ID"
                name="openedByUserId"
                value={form.openedByUserId}
                onChange={handleChange}
              />
            </div>
          )}

          <Textarea
            label="Notes"
            name="notes"
            value={form.notes}
            onChange={handleChange}
            helper="Optional notes for the opening session."
            rows={4}
          />

          {openState.isError && (
            <Alert tone="error">
              {getErrorMessage(openState.error, "Failed to open session.")}
            </Alert>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default CashingUpSessions;
